<template>
  <div>
    <!-- Confirmation Modal -->
    <ConfirmationModal
      :title="confirmationTitle"
      :message="confirmationMessage"
      confirmColor="error darken-1"
      ref="confirmationModal"
    />
    
    <!-- Asset Modal-->
    <v-dialog
      width="480px"
      :overlay-opacity="0.90"
      v-model="dialog"
    >
      <v-card>
        <v-card-title class="d-block">
          <div class="font-weight-bold">{{item.id ? "Update" : "Add"}} Asset</div>
          <p class="caption mb-4">Enter asset type, amount and description details below.</p>
        </v-card-title>
        <v-card-text class="py-2">

          <v-form
            @submit.prevent="addItem()"
          >
            <ValidationObserver ref="validationObserver">

              <ValidationProvider
                name="Asset Type"
                :rules="'required'"
                v-slot="{ errors }"
              >
                <v-select
                  outlined
                  dense
                  :autofocus="true"
                  label="Asset Type*"
                  placeholder="Select Asset type"
                  :items="assetTypes"
                  item-text="accountName"
                  item-value="accountName"
                  v-model="item.accountName"
                  :error-messages="errors"
                ></v-select>
              </ValidationProvider>

              <ValidationProvider
                name="Member Value"
                :rules="'required|min_value:0|max_value:999999999999'"
                v-slot="{ errors }"
              >
                <vuetify-money
                  type="number"
                  outlined
                  dense
                  label="Member Value*"
                  v-model="item.memberValue"
                  :error-messages="errors"
                  :options="VuetifyMoneyOptions"
                />
              </ValidationProvider>
              
              <ValidationProvider
                name="Internal Value"
                :rules="'required|min_value:0|max_value:999999999999'"
                v-slot="{ errors }"
              >
                <vuetify-money
                  type="number"
                  outlined
                  dense
                  label="Internal Value*"
                  v-model="item.internalValue"
                  :error-messages="errors"
                  :options="VuetifyMoneyOptions"
                />
              </ValidationProvider>
              <ValidationProvider
                name="Description"
                :rules="'max:1500'"
                v-slot="{ errors }"
              >
                <v-text-field
                  outlined
                  dense
                  label="Asset Description"
                  v-model="item.description"
                  :error-messages="errors"
                ></v-text-field>
              </ValidationProvider>
            </ValidationObserver>

            <v-card-actions class="pt-5 justify-end">
              <v-btn
                @click="closeModal()"
                min-width="120"
                min-height="42"
                depressed
                class="text-capitalize"
              >Cancel</v-btn>
              <v-btn
                type="submit"
                min-width="120"
                min-height="42"
                depressed
                color="primary"
                class="text-capitalize"
              >Save</v-btn>
            </v-card-actions>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
    
    <v-row align="center" justify="space-between">
      <v-col cols="12" md="5"><h3>Assets</h3></v-col>
      <v-col cols="12" md="6" class="text-md-right">
        <v-btn
          @click="openModal"
          class="text-capitalize"
          depressed
          color="primary"
          >Add Asset</v-btn
        >
      </v-col>
    </v-row>
    <v-card class="mt-5" outlined rounded="lg">
      <v-data-table :items="assets" hide-default-footer :headers="headers">
        <!-- Customized No Data slot -->
        <template #no-data>
          <v-row justify="center">
            <v-col>
              <NoData />
              <p class="text-center mt-5">No Asset Items</p>
            </v-col>
          </v-row>
        </template>
        
        <template #[`item.accountName`]="{ item }">
          <div class="my-4">
            <span class="text-capitalize">{{item.accountName}}</span>
          </div>
        </template>
        
        <template #[`item.memberValue`]="{ item }">
          {{asMoney(item.memberValue)}}
        </template>
        
        <template #[`item.internalValue`]="{ item }">
          {{asMoney(item.internalValue)}}
        </template>
        
        <template #[`item.description`]="{ item }">
          <span class="text-wrap text-capitalize">{{item.description || "No description"}}</span>
        </template>
        
        <!-- Action slot -->
        <template #[`item.actions`]="{ item }">
          <v-btn
            @click="openModal(item)"
            class="text-capitalize"
            text
            color="primary"
          >
            Edit</v-btn>
          <v-btn
            @click="removeItem(item)"
            class="text-capitalize"
            text
            color="error"
          >
            Remove</v-btn>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import PageMixin from '@/mixins/page.mixin';
import { VuetifyMoneyOptions } from '@/lib/constants/options';
import { cloneDeep } from 'lodash';
import { v4 } from 'uuid';

export default {
  mixins: [PageMixin],
  data() {
    return {
      /** @type {import('vuetify').DataTableHeader[]}*/
      headers: [
        { text: 'Name', value: 'accountName', sortable: false },
        { text: 'Description', value: 'description', sortable: false },
        { text: 'Member Value', value: 'memberValue', sortable: false },
        { text: 'Internal Value', value: 'internalValue', sortable: false },
        { text: 'Actions', value: 'actions', align: 'center', sortable: false }
      ],
      dialog: false,
      item: {},

      VuetifyMoneyOptions: VuetifyMoneyOptions,
      confirmationTitle: "",
      confirmationMessage: "",
    };
  },
  props: {
    assets: {
      type: Array,
      default: () =>([])
    },
    assetTypes: {
      type: Array,
      default: () =>([])
    } 
  },

  methods: {
    openModal(item) {
      this.$refs.validationObserver?.reset();
      this.item = cloneDeep(item) || {};
      this.dialog = true;
    },

    closeModal(item) {
      this.dialog = false;
      this.item = item || {};
      this.$refs.validationObserver?.reset();
    },

    async addItem() {
      const assets = cloneDeep(this.assets)
      const valid = await this.$refs.validationObserver.validate();
      if(!valid) return;
      
      if(this.item.id) {
        const index = assets.findIndex((el) => el.id == this.item.id)
        assets.splice(index, 1, {...this.item});
      } else {
        this.item.id = v4(); 
        assets.push(this.item);
      }

      this.$emit('update', assets) ;
      this.closeModal();
    },
  
    removeItem(item) {
      this.confirmationTitle = `Confirm Deletion`;
      this.confirmationMessage = `Are you sure you want to delete this asset '${item.accountName}'?`;
      this.$refs.confirmationModal.show(async () => {
        const assets = this.assets.filter((el) => el.id !== item.id)
        this.$emit('update', assets) 
      });
    }
  }
};
</script>
>
